import type {
  WarehouseCellResponse,
  WarehouseResponse,
  WarehouseRowResponse,
} from '@nebalkon-workspace/storage-api';
import { warehouseCellKey } from './staff-warehouse-cell-picker.util';

export interface WarehouseCellGridSlot {
  cell: WarehouseCellResponse | null;
  floor: number;
  colNumber: number;
  label: string;
  shortLabel: string;
  isOccupied: boolean;
}

export interface WarehouseCellGridRow {
  rowNumber: number;
  columns: number;
  floors: number;
  slots: WarehouseCellGridSlot[];
}

export function buildOccupiedCellIdSet(
  occupiedCells: WarehouseCellResponse[],
): ReadonlySet<string> {
  return new Set(occupiedCells.map((cell) => cell.id));
}

export function buildWarehouseCellGridRows(
  warehouse: Pick<WarehouseResponse, 'rows'>,
  cells: WarehouseCellResponse[],
  occupiedCellIds: ReadonlySet<string>,
): WarehouseCellGridRow[] {
  const cellByKey = new Map<string, WarehouseCellResponse>();
  for (const cell of cells) {
    cellByKey.set(warehouseCellKey(cell), cell);
  }

  return [...(warehouse.rows ?? [])]
    .sort(
      (left: WarehouseRowResponse, right: WarehouseRowResponse) =>
        left.row_number - right.row_number,
    )
    .map((row) => {
      const slots: WarehouseCellGridSlot[] = [];

      for (let floor = row.floors; floor >= 1; floor -= 1) {
        for (let col = 1; col <= row.columns; col += 1) {
          const key = `${row.row_number}-${floor}-${col}`;
          const cell = cellByKey.get(key) ?? null;
          slots.push({
            cell,
            floor,
            colNumber: col,
            label: key,
            shortLabel: `${floor}-${col}`,
            isOccupied: cell ? occupiedCellIds.has(cell.id) : false,
          });
        }
      }

      return {
        rowNumber: row.row_number,
        columns: row.columns,
        floors: row.floors,
        slots,
      };
    });
}

export function toggleCellSelection(
  selectedIds: ReadonlySet<string>,
  cellId: string,
): ReadonlySet<string> {
  const next = new Set(selectedIds);
  if (next.has(cellId)) {
    next.delete(cellId);
  } else {
    next.add(cellId);
  }
  return next;
}
